import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  BoxBalanceEntryDto,
  BoxBalanceSummaryDto,
  BoxResetDto,
  BoxResetListResponse,
  BoxResetReceiptDto,
  EmployeeBoxOverviewDto,
  ResetBoxBalanceInput,
} from '@gc/shared';

import { api, ApiError } from '@/lib/api';

/**
 * Shared's `BoxResetDto` plus the counted-cash columns (WAVE2-DESIGN §4),
 * mirroring the API's extension of the same name — `@gc/shared` is frozen
 * this wave, so both sides carry the three extra fields locally until they
 * can be folded in.
 *
 * `null` means the count was skipped. Render it as "not counted", NEVER as
 * zero: "counted zero cash" and "did not count" are different audit facts.
 */
export interface BoxResetAuditDto extends BoxResetDto {
  countedCashMd: string | null;
  varianceMd: string | null;
}

/**
 * Shared's box summary plus who performed its last reset — the API's
 * `BoxBalanceSummaryAuditDto`, carried locally for the same frozen-package
 * reason. Optional so a server from before 2026-09-23 still type-checks as
 * "unknown"; null when never reset or recorded before the row kept it.
 */
export interface BoxBalanceSummaryAuditDto extends BoxBalanceSummaryDto {
  lastResetByName?: string | null;
  entries: BoxBalanceEntryAuditDto[];
}

/**
 * A box entry plus the provider an ELECTRONIC entry went through — the API's
 * extension of the same name (2026-09-24). Absent on every other entry.
 */
export interface BoxBalanceEntryAuditDto extends BoxBalanceEntryDto {
  electronicProviderLabel?: string;
}

export interface BoxResetAuditReceiptDto extends BoxResetReceiptDto {
  reset: BoxResetAuditDto;
}

export interface BoxResetAuditListResponse extends BoxResetListResponse {
  rows: BoxResetAuditDto[];
  /** Sum of stored variances across counted resets only — uncounted rows contribute nothing. */
  varianceTotalMd: string;
  countedResets: number;
  uncountedResets: number;
}

/**
 * The caller's own box (SPEC §12.1).
 *
 * Re-read after every reset and whenever the screen is reopened: the figure is
 * signed off at handover, so a stale one is worse than a spinner.
 */
export function useMyBox() {
  const [data, setData] = useState<BoxBalanceSummaryAuditDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await api.get<BoxBalanceSummaryAuditDto>('/box-balance/me'));
      setError(null);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return { data, loading, error, reload: load };
}

/**
 * Every open box plus the venue main account. Requires `reports.view`.
 *
 * A failed load keeps an ERROR, never a silent null: swallowing a 500/403
 * here made the whole section vanish, and a section that disappears reads as
 * "no open boxes" — the error-before-empty rule this screen shares with the
 * rest of the app.
 */
export function useEmployeeBoxes(enabled: boolean) {
  const { t } = useTranslation();
  const [data, setData] = useState<EmployeeBoxOverviewDto | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!enabled) return;
    try {
      setData(await api.get<EmployeeBoxOverviewDto>('/box-balance/employees'));
      setError(null);
    } catch (caught) {
      setError(
        caught instanceof ApiError && caught.message
          ? caught.message
          : t('boxBalance.employees.loadFailed'),
      );
    }
  }, [enabled, t]);

  useEffect(() => {
    void load();
  }, [load]);

  return { data, error, reload: load };
}

/** Rows per page of the reset history. */
export const BOX_RESETS_PAGE_SIZE = 50;

/** The history's route for one page — exported so the paging is pinned by tests. */
export function boxResetsPath(page: number): string {
  return `/box-balance/resets?page=${page}&pageSize=${BOX_RESETS_PAGE_SIZE}`;
}

/**
 * The reset audit log. Requires `reports.view`. Same error rule as above.
 *
 * Paged (audit, 2026-09-23): it fetched page one and stopped, under a footer
 * that totals EVERY reset — past 50 resets the rows on screen would no longer
 * add up to the total beneath them, and the older ones were unreachable.
 */
export function useBoxResets(enabled: boolean, page = 1) {
  const { t } = useTranslation();
  const [data, setData] = useState<BoxResetAuditListResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!enabled) return;
    try {
      setData(await api.get<BoxResetAuditListResponse>(boxResetsPath(page)));
      setError(null);
    } catch (caught) {
      setError(
        caught instanceof ApiError && caught.message
          ? caught.message
          : t('boxBalance.history.loadFailed'),
      );
    }
  }, [enabled, page, t]);

  useEffect(() => {
    void load();
  }, [load]);

  return { data, error, reload: load };
}

/**
 * Reset a box. With no `employeeId` it is the caller's own box; naming one is
 * how the OWNER resets somebody else's (owner, 2026-09-23). The server is the
 * authority on who may: anyone else naming another box gets `owner_only`.
 */
export function resetBox(
  input: ResetBoxBalanceInput,
  employeeId?: string,
): Promise<BoxResetAuditReceiptDto> {
  return api.post<BoxResetAuditReceiptDto>(resetBoxPath(employeeId), input);
}

/** The route a reset posts to — exported so the choice is pinned by tests. */
export function resetBoxPath(employeeId?: string): string {
  return employeeId
    ? `/box-balance/employees/${encodeURIComponent(employeeId)}/reset`
    : '/box-balance/reset';
}

/** One employee's live box, for the owner's reset dialog. Requires `reports.view`. */
export function fetchEmployeeBox(employeeId: string): Promise<BoxBalanceSummaryAuditDto> {
  return api.get<BoxBalanceSummaryAuditDto>(
    `/box-balance/employees/${encodeURIComponent(employeeId)}`,
  );
}
